/**
 * Academy back office, D-105, D-109 and D-118: the exam the trainer works on (TCF Canada, IELTS
 * General Training, TEF Canada or German, Goethe-Zertifikat). Every tab reads it and filters its
 * lists with `?exam=` (and `?level=` for German: one exam per level, A1 to C2). The TCF stays the
 * default: without the parameter the API answers for the TCF, so the TCF requests keep their exact
 * URL. The IELTS, the TEF and German share the documents (passages) of several questions.
 */

import { type ExamEnum } from "@agencyos/api-client";
import { GOETHE_LEVELS, GOETHE_MODULES, type GoetheLevel, type GoetheSkill } from "@agencyos/core";
import { SegmentedControl } from "@agencyos/ui-web";
import { createContext, type ReactNode, useContext } from "react";
import { useTranslation } from "react-i18next";

export const EXAMS: ExamEnum[] = ["tcf_canada", "ielts_general", "tef_canada", "goethe"];
export const IELTS: ExamEnum = "ielts_general";
export const TEF: ExamEnum = "tef_canada";
export const GOETHE: ExamEnum = "goethe";

const ExamContext = createContext<{ exam: ExamEnum; level: GoetheLevel }>({ exam: "tcf_canada", level: "B1" });

export function ExamProvider({ exam, level = "B1", children }: { exam: ExamEnum; level?: GoetheLevel; children: ReactNode }) {
  return <ExamContext.Provider value={{ exam, level }}>{children}</ExamContext.Provider>;
}

/** The exam chosen at the top of the Academy page (TCF when a tab is rendered on its own). */
export function useAdminExam(): {
  exam: ExamEnum;
  ielts: boolean;
  tef: boolean;
  goethe: boolean;
  level: GoetheLevel;
  passages: boolean;
  params: { exam?: ExamEnum; level?: GoetheLevel };
  /** D-123: the content lists (questions, topics, mock exams, copies, trends, session packs)
   * always say their exam, the TCF included: without it the API lists every exam, and the TCF
   * tabs showed the IELTS, TEF and German items (graded with the TCF grid). */
  contentParams: { exam: ExamEnum; level?: GoetheLevel };
} {
  const { exam, level } = useContext(ExamContext);
  const goethe = exam === GOETHE;
  return {
    exam,
    ielts: exam === IELTS,
    tef: exam === TEF,
    goethe,
    level,
    passages: exam === IELTS || exam === TEF || goethe,
    params: goethe ? { exam, level } : examParams(exam),
    contentParams: goethe ? { exam, level } : { exam },
  };
}

/**
 * D-123: the texts of a tab that name the exam. The IELTS, the TEF and German have their own tab
 * names (« Sessions TEF & examens urgents »…); the TCF keeps its key. Before, the TEF and German
 * tabs showed « Sessions TCF Canada », « Tendances TCF Cameroun », « structure officielle du TCF ».
 */
export function useExamTexts() {
  const { t } = useTranslation();
  const { exam } = useContext(ExamContext);
  const ns = exam === IELTS ? "academyAdmin.ielts" : exam === TEF ? "tef.admin" : exam === GOETHE ? "goethe.admin" : "";
  return {
    /** The exam's full name (« TEF Canada », « Goethe-Zertifikat (allemand) »). */
    examName: t(`ielts.exams.${exam}.name`),
    /** A tab title: the exam's own tab name, or the TCF key. */
    title: (tab: string, tcfKey: string) => (ns ? t(`${ns}.tab.${tab}`) : t(tcfKey)),
    /** A skill: Listening… for the IELTS, Hören… for German, the TCF/TEF names otherwise. */
    skill: (skill: string) =>
      exam === IELTS
        ? t(`academyAdmin.ielts.skill.${skill}`)
        : exam === GOETHE && skill in GOETHE_MODULES
          ? `${GOETHE_MODULES[skill as GoetheSkill]} (${skill.toUpperCase()})`
          : t(`academyAdmin.skill.${skill}`),
  };
}

/** `{exam}` for the IELTS, the TEF and German, nothing for the TCF (the API's default). */
export function examParams(exam: ExamEnum): { exam?: ExamEnum } {
  return exam === "tcf_canada" ? {} : { exam };
}

/** The TCF / IELTS / TEF / German switch shown in the page header. */
export function ExamSwitch({ value, onChange, exams = EXAMS }: { value: ExamEnum; onChange: (exam: ExamEnum) => void; exams?: readonly ExamEnum[] }) {
  const { t } = useTranslation();
  return (
    <SegmentedControl
      label={t("academyAdmin.ielts.examSwitch")}
      value={value}
      options={exams.map((exam) => ({ value: exam, label: t(`academyAdmin.ielts.exam.${exam}`) }))}
      onChange={(exam) => onChange(exam)}
    />
  );
}

/** German (D-118): the Goethe-Zertifikat level the trainer works on. */
export function LevelSwitch({ value, onChange }: { value: GoetheLevel; onChange: (level: GoetheLevel) => void }) {
  const { t } = useTranslation();
  return (
    <SegmentedControl
      label={t("goethe.admin.level")}
      value={value}
      options={GOETHE_LEVELS.map((level) => ({ value: level, label: level }))}
      onChange={(level) => onChange(level as GoetheLevel)}
    />
  );
}
